import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TableCell, TableHeader } from "../../table/tableCellHeader";
import { useParams } from "react-router-dom";
import useOfflineServiceStore from "../../../stores/admin/offlineServiceStore";
import OfflineService from "../../../api/admin/offlineService";
import OrderStatus from "../../common/orderStatus/orderStatus";
import { useModalConfirmActionStore } from "../../../stores/admin/modalConfirmActionStore";
import AdminUpdateState from "./adminUpdateState";

const AdminTableServiceRecordInfo: React.FC = () => {
  const { t } = useTranslation();
  const { order, fetchOrder } = useOfflineServiceStore();

  const { file } = useParams<{ file: string }>();

  const offlineService = new OfflineService();
  const {
    setMessage,
    setModalOpen,
    setConfirmAction,
    waitTime,
    setShowWaitTimeInput,
  } = useModalConfirmActionStore();

  useEffect(() => {
    fetchOrder(Number(file));
  }, []);

  function downloadFile(id: any): void {
    offlineService.downloadFile(id);
  }

  function downloadEcuFile(id: any): void {
    offlineService.downloadEcuFile(id);
  }

  function setWaitTime(id: any): void {
    setMessage(`${t("What waiting time should be set for an order")} ${id}?`);
    setShowWaitTimeInput(true);
    setConfirmAction(async (waitTime: { hours: number; minutes: number }) => {
      const offlineService = new OfflineService();
      await offlineService.changeOrderWaitTime(
        id,
        waitTime.hours * 60 + waitTime.minutes
      );
      await fetchOrder(id);
    });
    setModalOpen(true);
  }

  return (
    <>
      <div className="flex-1 flex flex-col">
        <div className="rounded-xl overflow-hidden border border-tableline max-md:hidden">
          <table className="w-full">
            <thead className="bg-tablebg2">
              <tr>
                <TableHeader additionalClassNames="w-20">#</TableHeader>
                <TableHeader additionalClassNames="w-20">Email</TableHeader>
                <TableHeader additionalClassNames="w-20">Series</TableHeader>
                <TableHeader additionalClassNames="w-52">Ecu</TableHeader>
                <TableHeader additionalClassNames="w-20">Engine</TableHeader>
                <TableHeader additionalClassNames="w-52">Software</TableHeader>
                <TableHeader additionalClassNames="w-52">Options</TableHeader>
                <TableHeader additionalClassNames="w-32">Uploaded</TableHeader>
                <TableHeader additionalClassNames="w-32">Status</TableHeader>
                <TableHeader additionalClassNames="w-40">Action</TableHeader>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-tablebg" key="1">
                {order !== undefined && (
                  <>
                    <TableCell>#{order.id}</TableCell>
                    <TableCell>{order.email}</TableCell>
                    <TableCell>{order.series}</TableCell>
                    <TableCell>{order.ecu}</TableCell>
                    <TableCell>{order.engine}</TableCell>
                    <TableCell>{order.software}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap">
                        {order.options !== undefined &&
                          order.options.map((option: any, subindex: number) => (
                            <p className="py-1 px-2 m-1 text-primary bg-primary bg-opacity-30 rounded-xl">
                              {option}
                            </p>
                          ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>
                          {new Date(order.uploaded).toLocaleDateString("ru-RU")}
                        </span>
                        <span className="text-gray-500 text-sm">
                          {new Date(order.uploaded).toLocaleTimeString(
                            "ru-RU",
                            { hour: "2-digit", minute: "2-digit" }
                          )}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <OrderStatus status={order.status} />

                      <button
                        className="flex items-center justify-center border-success
                                              border border-solid rounded-lg px-3 cursor-pointer bg-success h-10 w-38 my-2"
                        onClick={() => setWaitTime(order.id)}
                      >
                        {t("Set wait time")}
                      </button>
                      {order.waitMinutes && (
                        <span>
                          {`${Math.floor(order.waitMinutes / 60)} ч. ${
                            order.waitMinutes % 60
                          } м.`}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex-1">
                        <button
                          className="flex items-center justify-center border-primary 
                            border border-solid rounded-lg px-3 cursor-pointer bg-primary h-10 w-38 my-2"
                          onClick={() => downloadFile(order.id)}
                        >
                          {t("Download")}
                        </button>

                        {order.isTwoEcuAvailable === true && order.isTwoEcu === false && (
                          <button
                            className="flex items-center justify-center border-secondary 
                            border border-solid rounded-lg px-3 cursor-not-allowed bg-secondary h-10 w-38 my-2"
                          >
                            {t("Download Ecu2")}
                          </button>
                        )}
                        
                        {order.isTwoEcu === true && (
                          <button
                            className="flex items-center justify-center border-primary 
                            border border-solid rounded-lg px-3 cursor-pointer bg-primary h-10 w-38 my-2"
                            onClick={() => downloadEcuFile(order.id)}
                          >
                            {t("Download Ecu2")}
                          </button>
                        )}

                        <AdminUpdateState
                          fileId={order.id}
                          status={order.status}
                        />
                      </div>
                    </TableCell>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="md:hidden">
          <div
            className="rounded-xl overflow-hidden border border-tableline bg-tablebg my-4 p-4
                  flex flex-wrap"
          >
            {order !== undefined && (
              <>
                <div className="p-2 w-1/2">
                  <p className="text-textsecondary">#</p>
                  <p># {order.id}</p>
                </div>
                <div className="p-2 w-1/2">
                  <p className="text-textsecondary">Email</p>
                  <p>{order.email}</p>
                </div>

                <div className="p-2 w-1/2">
                  <p className="text-textsecondary">Series</p>
                  <p>{order.series}</p>
                </div>
                <div className="p-2 w-1/2">
                  <p className="text-textsecondary">ECU</p>
                  <p>{order.ecu}</p>
                </div>
                <div className="p-2 w-1/2">
                  <p className="text-textsecondary">Engine</p>
                  <p>{order.engine}</p>
                </div>
                <div className="p-2 w-full">
                  <p className="text-textsecondary">Software</p>
                  <p>{order.software}</p>
                </div>
                <div className="p-2 w-full">
                  <p className="text-textsecondary">Options</p>
                  <div className="flex flex-wrap">
                    {order.options !== undefined &&
                      order.options.map((option: any, subindex: number) => (
                        <p className="py-1 px-2 m-1 text-primary bg-primary bg-opacity-30 rounded-xl">
                          {option}
                        </p>
                      ))}
                  </div>
                </div>
                <div className="p-2 w-1/2">
                  <p className="text-textsecondary">Uploaded</p>
                  <div className="flex flex-col">
                    <span>
                      {new Date(order.uploaded).toLocaleDateString("ru-RU")}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {new Date(order.uploaded).toLocaleTimeString("ru-RU", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
                <div className="p-2 w-1/2">
                  <p className="text-textsecondary">Status</p>
                  <p>
                    {" "}
                    <OrderStatus status={order.status} />
                  </p>
                </div>
                <div className="flex w-full justify-center border-t border-tableline pt-4">
                  <button
                    className="flex items-center justify-center border-primary 
                            border border-solid rounded-lg px-3 cursor-pointer bg-primary h-10 w-11/12 my-2 mx-2"
                    onClick={() => downloadFile(order.id)}
                  >
                    {t("Download")}
                  </button>

                  {order.isTwoEcu === true && (
                    <button
                      className="flex items-center justify-center border-primary 
                          border border-solid rounded-lg px-3 cursor-pointer bg-primary h-10 w-11/12 my-2 mx-2"
                      onClick={() => downloadFile(order.id)}
                    >
                      {t("Download Ecu2")}
                    </button>
                  )}

                  <AdminUpdateState fileId={order.id} status={order.status} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {order !== undefined &&
        order.comment !== undefined &&
        order.comment.length >= 1 && (
          <div className="flex flex-col border border-divider rounded-xl border-dashed my-4 p-2">
            <p className="text-lg p-2">
              {t("User left the following comment")}
            </p>
            <p className="p-2 text-textsecondary">{order.comment}</p>
          </div>
        )}
    </>
  );
};

export default AdminTableServiceRecordInfo;
